// API client utility for Basic Auth
import { useAuth } from '../contexts/AuthContext';

export interface ApiResponse<T = any> {
  data: T;
  status: number;
  statusText: string;
  headers?: Headers;
}

export class ApiClient {
  private baseUrl: string;
  private getAuthToken: () => string | null;
  private refreshAccessToken: () => Promise<boolean>;
  private isRefreshing: boolean = false;
  private refreshPromise: Promise<boolean> | null = null;

  constructor(
    baseUrl: string = '',
    getAuthToken: () => string | null,
    refreshAccessToken: () => Promise<boolean>
  ) {
    this.baseUrl = baseUrl;
    this.getAuthToken = getAuthToken;
    this.refreshAccessToken = refreshAccessToken;
  }

  private async makeRequest<T>(
    url: string,
    options: RequestInit & { timeoutMs?: number } = {},
    isRetry: boolean = false,
    useApiToken: boolean = false
  ): Promise<ApiResponse<T>> {
    let authToken: string | null;
    
    if (useApiToken) {
      // Use API token from environment variables for Shipium API calls
      const apiToken = process.env.REACT_APP_API_TOKEN;
      if (!apiToken) {
        throw new Error('API token not configured. REACT_APP_API_TOKEN environment variable is required.');
      }
      // The API token may already include "Basic " prefix, so check before adding it
      authToken = apiToken.startsWith('Basic ') ? apiToken : `Basic ${apiToken}`;
      console.log('[ApiClient] Using API token (first 30 chars):', authToken.substring(0, 30) + '...');
    } else {
      // Use user's auth token
      authToken = this.getAuthToken();
      if (!authToken) {
        throw new Error('No authentication token available');
      }
      console.log('[ApiClient] Using user auth token (first 30 chars):', authToken.substring(0, 30) + '...');
    }

    const { timeoutMs, signal: userSignal, ...restOptions } = options;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const timeoutController =
      typeof timeoutMs === 'number' && timeoutMs > 0
        ? new AbortController()
        : undefined;
    if (timeoutController) {
      timeoutId = setTimeout(() => timeoutController.abort(), timeoutMs);
    }

    let mergedSignal: AbortSignal | undefined = userSignal ?? timeoutController?.signal;
    if (userSignal && timeoutController) {
      const merged = new AbortController();
      const abortMerged = () => merged.abort();
      userSignal.addEventListener('abort', abortMerged);
      timeoutController.signal.addEventListener('abort', abortMerged);
      mergedSignal = merged.signal;
    }

    const requestOptions: RequestInit = {
      ...restOptions,
      signal: mergedSignal,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': authToken,
        ...restOptions.headers,
      },
    };

    try {
      const response = await fetch(`${this.baseUrl}${url}`, requestOptions);
      
      // Check if this is a blob request (for file downloads)
      const isBlobRequest = options.headers && 
        Object.entries(options.headers).some(([key, value]) => 
          key.toLowerCase() === 'accept' && 
          (value as string).includes('application/octet-stream')
        );
      
      let data;
      if (isBlobRequest) {
        if (!response.ok) {
          // Try to get error message from blob response
          const errorText = await response.text();
          let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
          try {
            const errorJson = JSON.parse(errorText);
            errorMessage = errorJson.message || errorJson.error || errorMessage;
          } catch {
            if (errorText) {
              errorMessage = errorText;
            }
          }
          const error = new Error(errorMessage);
          (error as any).response = {
            status: response.status,
            statusText: response.statusText,
            data: { message: errorMessage }
          };
          throw error;
        }
        data = await response.blob();
      } else {
        // Check if response is ok before parsing JSON
        if (!response.ok) {
          // Handle 401 Unauthorized - try to refresh token and retry (only once)
          // BUT: Skip token refresh if we're using API token (useApiToken = true)
          if (response.status === 401 && this.refreshAccessToken && !isBlobRequest && !isRetry && !useApiToken) {
            console.log('[ApiClient] Received 401, attempting token refresh...');
            
            // Clone the response to read error data if refresh fails
            const responseClone = response.clone();
            
            // Only refresh once at a time (prevent multiple refresh calls)
            if (!this.isRefreshing) {
              this.isRefreshing = true;
              this.refreshPromise = this.refreshAccessToken();
            }
            
            // Wait for refresh to complete
            let refreshSuccess = false;
            try {
              if (this.refreshPromise) {
                refreshSuccess = await this.refreshPromise;
              } else {
                console.error('[ApiClient] Refresh promise is null');
                refreshSuccess = false;
              }
            } catch (refreshError) {
              console.error('[ApiClient] Error during token refresh:', refreshError);
              refreshSuccess = false;
            }
            
            if (refreshSuccess) {
              // Retry the original request with new token
              const newAuthToken = this.getAuthToken();
              if (newAuthToken) {
                console.log('[ApiClient] Token refresh successful, retrying original request');
                // Reset refreshing state
                this.isRefreshing = false;
                this.refreshPromise = null;
                
                // Recursively retry with isRetry flag to prevent infinite loops
                // Pass useApiToken flag through to maintain API token usage
                return this.makeRequest<T>(url, options, true, useApiToken);
              } else {
                console.error('[ApiClient] Token refresh succeeded but no new token available');
              }
            } else {
              console.error('[ApiClient] Token refresh failed');
            }
            
            // Refresh failed - read error data and throw
            this.isRefreshing = false;
            this.refreshPromise = null;
            
            // Read error from cloned response
            let errorData;
            const contentType = responseClone.headers.get('content-type');
            
            try {
              if (contentType && contentType.includes('application/json')) {
                errorData = await responseClone.json();
              } else {
                const errorText = await responseClone.text();
                errorData = { 
                  message: errorText || `HTTP ${response.status}: ${response.statusText}`,
                  status: response.status,
                  statusText: response.statusText
                };
              }
            } catch (parseError) {
              errorData = {
                message: `HTTP ${response.status}: ${response.statusText}`,
                status: response.status,
                statusText: response.statusText
              };
            }
            
            const error = new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
            (error as any).response = {
              status: response.status,
              statusText: response.statusText,
              data: errorData
            };
            throw error;
          }
          
          // For other errors or if refresh didn't work, read error data
          let errorData;
          const contentType = response.headers.get('content-type');
          
          try {
            if (contentType && contentType.includes('application/json')) {
              errorData = await response.json();
            } else {
              const errorText = await response.text();
              errorData = { 
                message: errorText || `HTTP ${response.status}: ${response.statusText}`,
                status: response.status,
                statusText: response.statusText
              };
            }
          } catch (parseError) {
            errorData = {
              message: `HTTP ${response.status}: ${response.statusText}`,
              status: response.status,
              statusText: response.statusText
            };
          }
          
          const error = new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
          (error as any).response = {
            status: response.status,
            statusText: response.statusText,
            data: errorData
          };
          throw error;
        }
        
        data = await response.json();
      }
      
      // Reset refreshing state on successful response
      if (this.isRefreshing) {
        this.isRefreshing = false;
        this.refreshPromise = null;
      }
      
      return {
        data,
        status: response.status,
        statusText: response.statusText,
        headers: response.headers,
      };
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    } finally {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    }
  }

  async get<T>(
    url: string,
    options?: RequestInit & {
      params?: Record<string, any>;
      useApiToken?: boolean;
      timeoutMs?: number;
    }
  ): Promise<ApiResponse<T>> {
    // Extract params and build query string
    const useApiToken = options?.useApiToken || false;
    let finalUrl = url;
    if (options?.params) {
      const { params, useApiToken: _, ...restOptions } = options;
      const queryString = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryString.append(key, String(value));
        }
      });
      const query = queryString.toString();
      finalUrl = query ? `${url}${url.includes('?') ? '&' : '?'}${query}` : url;
      console.log('[ApiClient] GET request with params:', { originalUrl: url, finalUrl, params });
      return this.makeRequest<T>(finalUrl, { ...restOptions, method: 'GET' }, false, useApiToken);
    }
    const { useApiToken: _, ...restOptions } = options || {};
    return this.makeRequest<T>(url, { ...restOptions, method: 'GET' }, false, useApiToken);
  }

  async post<T>(url: string, data?: any, options?: RequestInit & { useApiToken?: boolean }): Promise<ApiResponse<T>> {
    const useApiToken = options?.useApiToken || false;
    const { useApiToken: _, ...restOptions } = options || {};
    
    if (useApiToken) {
      console.log('[ApiClient] POST request using API token:', url);
    }
    
    return this.makeRequest<T>(url, {
      ...restOptions,
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    }, false, useApiToken);
  }

  async put<T>(url: string, data?: any, options?: RequestInit): Promise<ApiResponse<T>> {
    return this.makeRequest<T>(url, {
      ...options,
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete<T>(url: string, options?: RequestInit): Promise<ApiResponse<T>> {
    return this.makeRequest<T>(url, { ...options, method: 'DELETE' });
  }
}

// Hook to create an API client instance
export const useApiClient = () => {
  const { getAuthToken, refreshAccessToken } = useAuth();
  
  // Use the API server URL (port 3001) for API calls
  const baseUrl = process.env.NODE_ENV === 'production' 
    ? '' // In production, API and frontend are served from the same origin
    : 'http://localhost:3001'; // In development, API is on port 3001
  
  return new ApiClient(baseUrl, getAuthToken, refreshAccessToken);
};
